import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Tabs, Tab } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import SiteService from "services/SiteService";
import BackDropLoader from "components/common/BackDropLoader";
import Lottie from "react-lottie";
import { SHARE_LOTTIE_OPTIONS, SHARE_TYPES } from "configurations/Constants";
import {
  setSharedSiteInfo,
  setShareSiteSuccess,
} from "reduxStore/slices/siteSlice";
import UserService from "services/UserService";
import {
  USER_DATA_FETCH_ERROR,
  PLEASE_SELECT_AT_LEAST_ONE_SITE,
  PLEASE_SEARCH_AND_SELECT_FARMER,
  PLEASE_SELECT_FARMER,
  SITE_SHARE_ERROR,
  LABEL_ENTER_MOBILE_NUMBER,
  LABEL_SELECT_SITES_TO_SHARE,
} from "configurations/MessageConstants";
import { Spinner } from "react-bootstrap";
import { StatusCodes } from "configurations/StatusCodes";
import { toast } from "react-toastify";
import SharedDetails from "./SharedDetails";

const ShareSites = ({ type, selected, show, closeShare }) => {
  const dispatch = useDispatch();
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [farmerName, setFarmerName] = useState("");
  const [farmerEmail, setFarmerEmail] = useState("");
  const [activeTab, setActiveTab] = useState("share");
  const [isSearching, setIsSearching] = useState(false);
  const allSites = useSelector((state) => state.site.sites);

  const defaultValues = {
    selectedSites: type === SHARE_TYPES.SINGLE ? [selected[0]] : [],
    mobileNumber: "",
    farmerSelected: false,
    farmerEmail: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm({ defaultValues, mode: "onChange" });

  const watchedValues = watch();

  useEffect(() => {
    if (type === SHARE_TYPES.SINGLE && selected[0]) {
      setValue("selectedSites", [selected[0]]);
    } else {
      setValue("selectedSites", []);
    }
  }, [selected, type, setValue]);

  const handleSearch = async () => {
    const mobileNumber = watchedValues.mobileNumber;
    if (!mobileNumber) return;
    setIsSearching(true);
    try {
      const userData = await UserService.getUserFromMobileNumber(mobileNumber);
      if (userData) {
        setShowCheckbox(true);
        setFarmerEmail(userData.email);
        setFarmerName(userData.name);
      }
    } catch (error) {
      if (error.response.status === StatusCodes.NOT_FOUND) {
        toast.error(error.response.data.detail);
      }
      console.error(USER_DATA_FETCH_ERROR, error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClose = () => {
    setShowCheckbox(false);
    setFarmerName("");
    setFarmerEmail("");
    setActiveTab("share");
    reset(defaultValues);
    closeShare();
  };

  const handleClearCheckbox = () => {
    setValue("farmerSelected", false);
    setValue("farmerEmail", "");
    setFarmerName("");
    setFarmerEmail("");
    setShowCheckbox(false);
  };

  const handleSiteSelect = (site) => {
    const currentSelected = watchedValues.selectedSites || [];
    const isSelected = currentSelected.some(
      (selectedSite) => selectedSite.id === site.id
    );
    const newSelectedSites = isSelected
      ? currentSelected.filter((selectedSite) => selectedSite.id !== site.id)
      : [...currentSelected, site];
    setValue("selectedSites", newSelectedSites, {
      shouldValidate: isSubmitted,
    });
  };

  const onSubmit = async (data) => {
    if (!data.selectedSites?.length || !data.farmerEmail) {
      return;
    }
    try {
      const payload = {
        userEmail: data.farmerEmail,
        shareSites: data.selectedSites.map((site) => ({
          siteId: site.id,
          siteName: site.name,
          editor: false,
        })),
      };
      const response = await SiteService.shareSites(payload);
      handleShare(response, farmerName);
    } catch (error) {
      console.error(SITE_SHARE_ERROR, error);
      toast.error(error.response.data.detail);
    }
  };

  const handleShare = (response, farmerName) => {
    const groupedDetails = response.details.reduce(
      (group, { reason, siteName }) => {
        if (!group[reason]) {
          group[reason] = { reason, siteNames: [] };
        }
        group[reason].siteNames.push(siteName);
        return group;
      },
      {}
    );
    const details = Object.values(groupedDetails);
    dispatch(
      setSharedSiteInfo({
        success: response.success,
        details,
        farmerName,
      })
    );
    dispatch(setShareSiteSuccess(true));
    handleClose();
  };

  const renderShareContent = () => (
    <>
      {isSubmitting && (
        <BackDropLoader
          loader={
            <Lottie options={SHARE_LOTTIE_OPTIONS} height={100} width={100} />
          }
        />
      )}
      <Controller
        name="selectedSites"
        control={control}
        rules={{
          validate: (value) =>
            value && value.length > 0 ? true : PLEASE_SELECT_AT_LEAST_ONE_SITE,
        }}
        render={() => null}
      />
      <div>
        {errors.selectedSites ? (
          <div className="validation-error">{errors.selectedSites.message}</div>
        ) : (
          <small>{LABEL_SELECT_SITES_TO_SHARE}</small>
        )}
      </div>
      <div className="mb-3 share-container">
        {(type === SHARE_TYPES.SINGLE ? selected : allSites)
          ?.filter((site) => !site.isShared)
          .map((site) => (
            <div
              key={site.id}
              className="p-2 rounded border d-flex justify-content-between my-2"
            >
              <div className="d-flex align-items-start text-truncate">
                <Form.Check
                  type="checkbox"
                  aria-label="Select Site"
                  checked={(watchedValues.selectedSites || []).some(
                    (selectedSite) => selectedSite.id === site.id
                  )}
                  onChange={() => handleSiteSelect(site)}
                  className="me-2"
                />
                <div className="text-truncate">
                  <div className="text-truncate">{site.name}</div>
                  <small className="text-muted d-flex gap-1 align-items-center">
                    <span className="text-truncate">{site.farmName}</span>|
                    <span className="text-truncate">{site.businessName}</span>
                  </small>
                </div>
              </div>
              <div className="ms-2">
                <small className="text-muted text-nowrap">
                  {site.totalArea} Acres
                </small>
              </div>
            </div>
          ))}
      </div>
      <Form.Group className="mb-3">
        <Form.Label>Mobile Number</Form.Label>
        <div className="d-flex gap-2">
          <Controller
            name="mobileNumber"
            control={control}
            rules={{
              required: "Mobile number is required",
              validate: (value) =>
                value.length === 10 || "Mobile number must be 10 digits",
            }}
            render={({ field }) => (
              <div className="w-100">
                <Form.Control
                  {...field}
                  type="text"
                  placeholder={LABEL_ENTER_MOBILE_NUMBER}
                  maxLength={10}
                  className="input-underline"
                  isInvalid={!!errors.mobileNumber}
                  onChange={(e) => {
                    const number = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    field.onChange(number);
                    handleClearCheckbox();
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.mobileNumber?.message}
                </Form.Control.Feedback>
              </div>
            )}
          />
          <div>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={handleSearch}
              aria-label="Search Mobile Number"
              disabled={
                isSearching ||
                errors.mobileNumber ||
                !watchedValues.mobileNumber
              }
            >
              {isSearching ? (
                <Spinner
                  animation="border"
                  size="sm"
                  className="align-middle"
                />
              ) : (
                <i className="fa fa-search" aria-hidden="true"></i>
              )}
            </Button>
          </div>
        </div>
      </Form.Group>
      {showCheckbox && (
        <Form.Group className="mb-3">
          <Controller
            name="farmerSelected"
            control={control}
            rules={{ required: PLEASE_SELECT_FARMER }}
            render={({ field }) => (
              <Form.Check
                {...field}
                type="checkbox"
                aria-label="Select Farmer"
                label={farmerName}
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  setValue("farmerEmail", e.target.checked ? farmerEmail : "");
                }}
                isInvalid={!!errors.farmerSelected}
              />
            )}
          />
        </Form.Group>
      )}
    </>
  );

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="md"
        aria-labelledby="share-sites-popup"
      >
        <Modal.Header closeButton>
          <Modal.Title id="share-sites-popup">
            {type === SHARE_TYPES.SINGLE ? "Share Site" : "Share Sites"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            {type === SHARE_TYPES.SINGLE ? (
              <Tabs
                id="site-tabs"
                activeKey={activeTab}
                onSelect={setActiveTab}
                className="mb-3"
              >
                <Tab eventKey="share" title="Share">
                  {renderShareContent()}
                </Tab>
                <Tab eventKey="details" title="Details">
                  <SharedDetails site={selected[0]} />
                </Tab>
              </Tabs>
            ) : (
              renderShareContent()
            )}
          </Modal.Body>
          {activeTab === "share" && (
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                Share
              </Button>
            </Modal.Footer>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default ShareSites;
